import React, { useEffect, useState } from 'react';
import { Modal, Stack, Textarea, TextInput, Select, Button, Group, Text } from '@mantine/core';
import { api } from '../../utils/api';
import { useUserStore } from '../../store/userStore';
import { showToast } from '../../utils/toast';

interface DocsFeedbackModalProps {
  opened: boolean;
  onClose: () => void;
  pageUrl: string;
  pageTitle?: string;
}

type FeedbackType = 'bug' | 'suggestion' | 'question';

export const DocsFeedbackModal: React.FC<DocsFeedbackModalProps> = ({
  opened,
  onClose,
  pageUrl,
  pageTitle,
}) => {
  const currentUser = useUserStore((s) => s.user);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [type, setType] = useState<FeedbackType>('bug');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ message?: string; email?: string }>({});

  useEffect(() => {
    if (opened) {
      setMessage('');
      setType('bug');
      setEmail(currentUser?.email || '');
      setErrors({});
    }
  }, [opened, currentUser?.email]);

  const validate = () => {
    const next: { message?: string; email?: string } = {};
    const trimmed = message.trim();
    if (trimmed.length < 5) next.message = 'Минимум 5 символов';
    if (trimmed.length > 5000) next.message = 'Максимум 5000 символов';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = 'Невалидный email';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await api('/docs/feedback', {
        method: 'POST',
        body: {
          message: message.trim(),
          email: email.trim(),
          type,
          pageUrl,
          pageTitle,
        },
      });
      showToast({ title: 'Фидбек отправлен. Спасибо!', message: '' });
      onClose();
    } catch (e) {
      console.error('Feedback send failed', e);
      showToast({
        title: 'Не удалось отправить',
        message: 'Попробуйте чуть позже.',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Фидбек по документации" size="md" centered>
      <Stack gap="md">
        {pageTitle && (
          <Text size="xs" c="dimmed">
            Статья: {pageTitle}
          </Text>
        )}
        <Select
          label="Тип"
          value={type}
          onChange={(v) => v && setType(v as FeedbackType)}
          data={[
            { value: 'bug', label: 'Ошибка в тексте' },
            { value: 'suggestion', label: 'Предложение' },
            { value: 'question', label: 'Вопрос' },
          ]}
          allowDeselect={false}
        />
        <TextInput
          label="Ваш email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          error={errors.email}
          required
        />
        <Textarea
          label="Сообщение"
          placeholder="Что не так или что предложить?"
          value={message}
          onChange={(e) => setMessage(e.currentTarget.value)}
          minRows={5}
          autosize
          maxRows={12}
          error={errors.message}
          required
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={onClose} disabled={isSubmitting}>
            Отмена
          </Button>
          <Button onClick={handleSubmit} loading={isSubmitting}>
            Отправить
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
